import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Formik, useFormikContext } from 'formik'
import { ProjectDetailsSchema } from '@/lib/validations'
import {
  useCreateProjectDetailsMutation,
  useUpdateProjectDetailsMutation,
  useGetSyncMilestonesQuery,
  type ProjectDetails,
} from '@/redux/service/project'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import toast from 'react-hot-toast'
import { CalendarIcon, Loader2, PlusCircle, Pencil, X } from 'lucide-react'
import { Calendar } from './ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover'
import { cn, toISTDateOnly } from '@/lib/utils'
import { format } from 'date-fns'
import ReactSelect from 'react-select'

interface Props {
  open: boolean
  onClose: () => void
  project_id?: number | undefined
  list_id?: number | string | undefined
  detail?: ProjectDetails | null | undefined
  project_type?: string | undefined
}

const AutoCalculator = ({
  project_type,
}: {
  project_type: string | undefined
}) => {
  const { values, setFieldValue } = useFormikContext<{
    total_budget: number | string
    total_hours: number | string
    hourly_cost: number | string
  }>()

  const isUpworkTracking = project_type === 'Upwork Tracking'

  useEffect(() => {
    const hours = Number(values.total_hours) || 0
    const hourlyCost = Number(values.hourly_cost) || 0
    const budget = Number(values.total_budget) || 0

    if (isUpworkTracking) {
      if (hours > 0 && hourlyCost > 0) {
        const calculatedBudget = hours * hourlyCost
        if (calculatedBudget !== budget) {
          setFieldValue('total_budget', calculatedBudget)
        }
      }
    } else {
      if (hours > 0 && budget > 0) {
        const calculatedHourlyCost = Math.round((budget / hours) * 100) / 100
        if (calculatedHourlyCost !== hourlyCost) {
          setFieldValue('hourly_cost', calculatedHourlyCost)
        }
      }
    }
  }, [
    values.total_hours,
    values.hourly_cost,
    values.total_budget,
    isUpworkTracking,
    setFieldValue,
  ])

  return null
}

/* ── Small helper: field label with optional required star and badge ── */
const FieldLabel = ({
  htmlFor,
  children,
  required,
  badge,
}: {
  htmlFor?: string
  children: React.ReactNode
  required?: boolean
  badge?: string | undefined
}) => (
  <Label
    htmlFor={htmlFor}
    className='text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5'
  >
    {children}
    {required && <span className='text-destructive'>*</span>}
    {badge && (
      <span className='text-[10px] font-medium normal-case tracking-normal px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border'>
        {badge}
      </span>
    )}
  </Label>
)

const CreateUpdateProject = ({
  open,
  onClose,
  project_id,
  list_id,
  detail,
  project_type,
}: Props) => {
  const { data: milestonesData, isLoading: isLoadingMilestones } =
    useGetSyncMilestonesQuery(list_id ?? '', { skip: !list_id || !open })
  const [createProjectDetails, { isLoading: isCreating }] =
    useCreateProjectDetailsMutation()
  const [updateProjectDetails, { isLoading: isUpdating }] =
    useUpdateProjectDetailsMutation()

  const [startDateOpen, setStartDateOpen] = useState(false)
  const [endDateOpen, setEndDateOpen] = useState(false)
  const isEdit = !!detail?.id
  const isLoading = isCreating || isUpdating
  const isUpworkTracking = project_type === 'Upwork Tracking'

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='max-h-[92vh] overflow-y-auto overflow-x-hidden p-0 gap-0'>
        {' '}
        {/* ── Dialog header ── */}
        <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 shrink-0'>
              {isEdit ? (
                <Pencil className='w-4 h-4 text-primary' />
              ) : (
                <PlusCircle className='w-4 h-4 text-primary' />
              )}
            </div>
            <div>
              <DialogTitle className='text-base font-semibold leading-tight'>
                {isEdit ? 'Update Project Details' : 'Add Project Details'}
              </DialogTitle>
              <DialogDescription className='text-xs text-muted-foreground mt-0.5'>
                {isEdit
                  ? 'Edit the fields below and save your changes.'
                  : 'Fill in the details to create a new project entry.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <Formik
          initialValues={{
            total_budget: detail?.total_budget ?? '',
            total_hours: detail?.total_hours ?? '',
            description: detail?.description ?? '',
            hourly_cost: detail?.hourly_cost ?? '',
            start_date: toISTDateOnly(detail?.start_date),
            end_date: toISTDateOnly(detail?.end_date),
            milestone_id: (() => {
              const fromMilestones = detail?.milestones
                ?.filter((m): m is NonNullable<typeof m> => m != null)
                .map(m => m.id)
                .filter((id): id is NonNullable<typeof id> => id != null)
              if (fromMilestones && fromMilestones.length > 0) {
                return fromMilestones
              }
              if (Array.isArray(detail?.milestone_id)) {
                return detail.milestone_id.filter(
                  (id): id is NonNullable<typeof id> => id != null
                )
              }
              return detail?.milestone_id ? [detail.milestone_id] : []
            })(),
          }}
          validationSchema={ProjectDetailsSchema}
          onSubmit={async values => {
            if (!project_id) {
              toast.error('Project ID is required')
              return
            }
            try {
              const payload: ProjectDetails = {
                total_budget: Number(values.total_budget),
                total_hours: Number(values.total_hours),
                description: values.description || undefined,
                hourly_cost: values.hourly_cost
                  ? Number(values.hourly_cost)
                  : undefined,
                start_date: values.start_date || null,
                end_date: values.end_date || null,
                milestone_id:
                  values.milestone_id && values.milestone_id.length > 0
                    ? values.milestone_id
                    : undefined,
              }

              if (isEdit) {
                await updateProjectDetails({
                  project_id,
                  data: { ...payload, id: detail?.id } as ProjectDetails,
                }).unwrap()
              } else {
                await createProjectDetails({
                  project_id,
                  data: payload,
                }).unwrap()
              }

              toast.success('Project details saved')
              onClose()
            } catch (err: unknown) {
              const error = err as { data?: { message?: string } }
              toast.error(error?.data?.message ?? 'Failed')
            }
          }}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            errors,
            touched,
            setFieldValue,
          }) => (
            <div className='px-6 py-5 space-y-5 overflow-x-hidden'>
              {/* Auto calculator — invisible side effect */}
              <AutoCalculator project_type={project_type} />

              {/* ── Budget + Hours ── */}
              <div className='grid grid-cols-2 gap-4'>
                {/* Budget */}
                <div className='space-y-1.5'>
                  <FieldLabel
                    htmlFor='total_budget'
                    required={!isUpworkTracking}
                    badge={isUpworkTracking ? 'Auto' : undefined}
                  >
                    Budget ($)
                  </FieldLabel>
                  <Input
                    id='total_budget'
                    name='total_budget'
                    type='number'
                    min={0}
                    step='0.01'
                    placeholder='0.00'
                    value={values.total_budget}
                    onChange={handleChange}
                    disabled={isUpworkTracking}
                    className={cn(
                      'h-9 text-sm',
                      errors.total_budget && touched.total_budget
                        ? 'border-destructive focus-visible:ring-destructive/30'
                        : '',
                      isUpworkTracking
                        ? 'bg-muted cursor-not-allowed opacity-70'
                        : ''
                    )}
                  />
                  {errors.total_budget && touched.total_budget && (
                    <p className='text-xs text-destructive'>
                      {errors.total_budget}
                    </p>
                  )}
                </div>

                {/* Hours */}
                <div className='space-y-1.5'>
                  <FieldLabel htmlFor='total_hours' required>
                    Total Hours
                  </FieldLabel>
                  <Input
                    id='total_hours'
                    name='total_hours'
                    type='number'
                    min={0}
                    placeholder='0'
                    value={values.total_hours}
                    onChange={handleChange}
                    className={cn(
                      'h-9 text-sm',
                      errors.total_hours && touched.total_hours
                        ? 'border-destructive focus-visible:ring-destructive/30'
                        : ''
                    )}
                  />
                  {errors.total_hours && touched.total_hours && (
                    <p className='text-xs text-destructive'>
                      {errors.total_hours}
                    </p>
                  )}
                </div>
              </div>

              {/* ── Description ── */}
              <div className='space-y-1.5'>
                <FieldLabel htmlFor='description'>Description</FieldLabel>
                <Input
                  id='description'
                  name='description'
                  placeholder='Enter a short description…'
                  value={values.description}
                  onChange={handleChange}
                  className='h-9 text-sm'
                />
              </div>

              {/* ── Milestones ── */}
              <div className='space-y-1.5'>
                <FieldLabel htmlFor='milestone_id'>Milestone</FieldLabel>
                <ReactSelect
                  isMulti
                  name='milestone_id'
                  options={milestonesData?.data?.map(m => ({
                    value: m.id,
                    label: m.name,
                  }))}
                  value={milestonesData?.data
                    ?.filter(m => values.milestone_id?.includes(m.id))
                    .map(m => ({ value: m.id, label: m.name }))}
                  onChange={options => {
                    setFieldValue(
                      'milestone_id',
                      options
                        ? options.map(
                            (o: { value: string; label: string } | undefined) =>
                              o?.value
                          )
                        : []
                    )
                  }}
                  isLoading={isLoadingMilestones}
                  placeholder={
                    isLoadingMilestones
                      ? 'Loading milestones…'
                      : 'Select milestones'
                  }
                  className='text-sm'
                  classNames={{
                    control: () =>
                      '!min-h-[36px] !bg-background !border-input !rounded-md !shadow-none hover:!border-primary/50 !transition-colors',
                    menu: () =>
                      '!bg-popover !border !border-border !rounded-lg !shadow-lg !z-50',
                    option: state =>
                      state.isFocused
                        ? '!bg-accent !text-accent-foreground'
                        : '!bg-transparent !text-popover-foreground',
                    multiValue: () =>
                      '!bg-primary/10 !text-primary !rounded-md !border !border-primary/20',
                    multiValueLabel: () =>
                      '!text-primary !text-xs !font-medium',
                    multiValueRemove: () =>
                      '!text-primary/60 hover:!text-primary hover:!bg-primary/20 !rounded-r-md',
                    placeholder: () => '!text-muted-foreground !text-sm',
                    input: () => '!text-sm',
                  }}
                  styles={{
                    valueContainer: base => ({
                      ...base,
                      display: 'flex',
                      flexWrap: 'wrap',
                      overflow: 'hidden',
                      maxWidth: '100%',
                    }),
                    control: base => ({
                      ...base,
                      minHeight: 36,
                      maxWidth: '100%',
                    }),
                    container: base => ({
                      ...base,
                      width: '100%',
                    }),
                  }}
                />
              </div>

              {/* ── Hourly Rate ── */}
              <div className='space-y-1.5'>
                <FieldLabel
                  htmlFor='hourly_cost'
                  required={isUpworkTracking}
                  badge={!isUpworkTracking ? 'Auto' : undefined}
                >
                  Hourly Rate ($)
                </FieldLabel>
                <Input
                  id='hourly_cost'
                  name='hourly_cost'
                  type='number'
                  min={0}
                  placeholder='0.00'
                  value={values.hourly_cost}
                  onChange={handleChange}
                  disabled={!isUpworkTracking}
                  className={cn(
                    'h-9 text-sm',
                    !isUpworkTracking
                      ? 'bg-muted cursor-not-allowed opacity-70'
                      : ''
                  )}
                />
              </div>

              {/* ── Date range ── */}
              <div className='grid grid-cols-2 gap-4'>
                {/* Start Date */}
                <div className='space-y-1.5'>
                  <FieldLabel>Start Date</FieldLabel>
                  <div className='relative'>
                    <Popover
                      open={startDateOpen}
                      onOpenChange={setStartDateOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          type='button'
                          variant='outline'
                          className={cn(
                            'h-9 w-full justify-start text-left font-normal text-sm border-input hover:bg-accent/30 transition-colors',
                            values.start_date && 'pr-8',
                            !values.start_date && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className='mr-2 h-3.5 w-3.5 opacity-50 shrink-0' />
                          {values.start_date
                            ? format(new Date(values.start_date), 'PP')
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      {values.start_date && (
                        <button
                          type='button'
                          onClick={e => {
                            e.stopPropagation()
                            setFieldValue('start_date', '')
                          }}
                          className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors'
                          aria-label='Clear start date'
                        >
                          <X className='h-3.5 w-3.5' />
                        </button>
                      )}
                      <PopoverContent
                        align='start'
                        className='w-auto p-0 border border-border shadow-lg rounded-xl'
                      >
                        <Calendar
                          mode='single'
                          captionLayout='dropdown'
                          fromYear={2018}
                          toYear={2035}
                          selected={
                            values.start_date
                              ? new Date(values.start_date)
                              : undefined
                          }
                          onSelect={date => {
                            setFieldValue(
                              'start_date',
                              date ? format(date, 'yyyy-MM-dd') : ''
                            )

                            setStartDateOpen(false) // close popover
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* End Date */}
                <div className='space-y-1.5'>
                  <FieldLabel>End Date</FieldLabel>
                  <div className='relative'>
                    <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type='button'
                          variant='outline'
                          className={cn(
                            'h-9 w-full justify-start text-left font-normal text-sm border-input hover:bg-accent/30 transition-colors',
                            values.end_date && 'pr-8',
                            !values.end_date && 'text-muted-foreground'
                          )}
                        >
                          <CalendarIcon className='mr-2 h-3.5 w-3.5 opacity-50 shrink-0' />
                          {values.end_date
                            ? format(new Date(values.end_date), 'PP')
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      {values.end_date && (
                        <button
                          type='button'
                          onClick={e => {
                            e.stopPropagation()
                            setFieldValue('end_date', '')
                          }}
                          className='absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors'
                          aria-label='Clear end date'
                        >
                          <X className='h-3.5 w-3.5' />
                        </button>
                      )}
                      <PopoverContent
                        align='start'
                        className='w-auto p-0 border border-border shadow-lg rounded-xl'
                      >
                        <Calendar
                          mode='single'
                          captionLayout='dropdown'
                          fromYear={2018}
                          toYear={2035}
                          selected={
                            values.end_date
                              ? new Date(values.end_date)
                              : undefined
                          }
                          onSelect={date => {
                            setFieldValue(
                              'end_date',
                              date ? format(date, 'yyyy-MM-dd') : ''
                            )

                            setEndDateOpen(false)
                          }}
                          disabled={date =>
                            values.start_date
                              ? date < new Date(values.start_date)
                              : false
                          }
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  {errors.end_date && touched.end_date && (
                    <p className='text-xs text-destructive'>
                      {errors.end_date}
                    </p>
                  )}
                </div>
              </div>

              {/* ── Actions ── */}
              <div className='flex justify-end gap-2 pt-2 border-t border-border'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={onClose}
                  className='h-8 text-xs border-border hover:bg-accent/30'
                >
                  Cancel
                </Button>
                <Button
                  size='sm'
                  onClick={() => handleSubmit()}
                  disabled={isLoading}
                  className='h-8 text-xs min-w-[80px]'
                >
                  {isLoading ? (
                    <>
                      <Loader2 className='w-3.5 h-3.5 mr-1.5 animate-spin' />
                      Saving…
                    </>
                  ) : isEdit ? (
                    'Update'
                  ) : (
                    'Save'
                  )}
                </Button>
              </div>
            </div>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default CreateUpdateProject
